import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { validationMessages } from '@sparklayer-io/spark-dash-core/types/errors';
import { PasswordStrengthMeter } from '@sparklayer-io/spark-dash-pages/components/password-strength-meter';
import { PASSWORD_MIN_LENGTH } from '@sparklayer-io/spark-dash-pages/constants/password';
import { Alert } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { AuthButton } from './auth-button';
import { FloatingInput } from './floating-input';
import { TermsCheckbox } from './terms-checkbox';

const COMPONENT = 'features:auth.components.invite_register_form' satisfies I18nPrefix;

type InviteRegisterFormValues = {
  email: string;
  name: string;
  password: string;
};

type InviteRegisterFormProps = {
  handlers: DashboardHandlers;
  email: string;
};

export function InviteRegisterForm({ handlers, email }: InviteRegisterFormProps) {
  const { apiHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);

  const form = useAppForm({
    defaultValues: {
      email,
      name: '',
      password: '',
    } satisfies InviteRegisterFormValues,
    onSubmit: async ({ value }) => {
      setBannerMessages([]);
      setSuccess(null);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });

        await apiHandler.fetch({
          method: 'POST',
          path: '/register',
          payload: {
            email: value.email,
            name: value.name,
            password: value.password,
          },
        });

        setSuccess(t(`${COMPONENT}.success`));
      } catch (err) {
        const messages = validationMessages(err);
        if (messages) {
          setBannerMessages(messages);
        } else {
          setBannerMessages([t('errors:generic')]);
        }
      }
      setLoading(false);
    },
  });

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      {success && (
        <Alert variant="success" headingLevel="h3">
          <p className="text-sm">{success}</p>
        </Alert>
      )}
      {bannerMessages.length > 0 && (
        <Alert variant="warning" headingLevel="h3">
          {bannerMessages.map((message) => (
            <p key={message} className="text-sm">
              {message}
            </p>
          ))}
        </Alert>
      )}

      <form.Field name="email">
        {(field) => (
          <FloatingInput
            name="email"
            type="email"
            label={t(`${COMPONENT}.email_label`)}
            autoComplete="email"
            required
            disabled
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <form.Field name="name">
        {(field) => (
          <FloatingInput
            name="name"
            label={t(`${COMPONENT}.name_label`)}
            autoComplete="name"
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <form.Field name="password">
        {(field) => (
          <FloatingInput
            name="password"
            type="password"
            label={t(`${COMPONENT}.password_label`)}
            minLength={PASSWORD_MIN_LENGTH}
            maxLength={128}
            required
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      </form.Field>

      <form.Subscribe selector={(state) => state.values.password}>
        {(password) => <PasswordStrengthMeter password={password} />}
      </form.Subscribe>

      <TermsCheckbox
        i18nKey="auth.components.common.terms.agreement"
        linkHref="https://sparklayer.io/terms"
      />

      <div>
        <AuthButton loading={loading}>{t(`${COMPONENT}.submit`)}</AuthButton>
      </div>
    </form>
  );
}
